import { PureComponent } from 'react'
import * as cn from 'classnames'
import * as isNull from 'lodash/isNull'
import * as last from 'lodash/last'
import * as reject from 'lodash/reject'
import * as debounce from 'lodash/debounce'
import * as styles from 'components/features/pfm/MonthlyBarChart/MonthlyBarChart.css'
import { formatUSCurrency } from 'helpers/formatHelpers'
import SingleMonthBarChart from 'components/atoms/charts/SingleMonthBarChart'
import { DateBasedAmount, TrendLine } from 'interfaces/trend'
import ChartHeader from 'components/atoms/charts/ChartHeader'
import * as moment from 'moment'
import { CHART_LABELS_TILT_THRESHOLD } from 'constants/component'
import LineChartLegend, { LineLegend } from 'components/atoms/legends/LineChartLegend'
import { SecondaryLine } from 'interfaces/chart'
import HeadedChartLayout, { HeadedChartLayoutComponent } from 'components/layouts/HeadedChartLayout'
import { createTouchMoveXHandler } from 'helpers/touchHelpers'
import MonthlyBarChartLines from 'components/atoms/charts/MonthlyBarChartLines'
import Touchable from 'components/utils/Touchable'

export type MonthlyBarChartProps = {
  memberActualValues: TrendLine
  memberProjectedValue?: DateBasedAmount
  peerActualValues: TrendLine
  peerProjectedValue?: DateBasedAmount
  otherActualValues: TrendLine
  otherProjectedValue?: DateBasedAmount

  limitLine?: SecondaryLine
  compareLine?: SecondaryLine

  isMobile?: boolean
  selectedPeergroupName: string

  withComparison: boolean
}

type AmountProjectedPair = {
  amount: number
  projected?: number
}

const MAX_BARS_BEFORE_HIDDEN_LABELS = 18
const MAX_BARS_BEFORE_CHART_PADDING = 10

type MonthlyBarChartState = {
  defaultSelectedDate: string
  selectedDate: string | null
  shouldTiltLabels: boolean
}

class MonthlyBarChart extends PureComponent<MonthlyBarChartProps, MonthlyBarChartState> {
  static getDerivedStateFromProps (nextProps: MonthlyBarChartProps, prevState: MonthlyBarChartState) {
    // discard the selection
    if (!nextProps.memberActualValues.find(({ date }) => date === prevState.selectedDate)) {
      return {
        selectedDate: null,
      }
    }
    return {}
  }

  private chartContainerNode: HTMLElement | null = null
  private chartRect: ClientRect
  private barWidth: number

  private get barsCount () {
    return this.props.memberActualValues.length
  }

  private get hasLegend () {
    const {
      isMobile,
      compareLine,
      limitLine,
    } = this.props

    return isMobile && Boolean(compareLine || limitLine)
  }

  private get hasProjected () {
    const { memberProjectedValue, peerProjectedValue, otherProjectedValue } = this.props

    return Boolean(memberProjectedValue || peerProjectedValue || otherProjectedValue)
  }

  constructor (props: MonthlyBarChartProps) {
    super(props)

    // the last one by default
    const defaultSelectedDate = last(this.props.memberActualValues).date
    this.state = {
      defaultSelectedDate,
      selectedDate: null,
      shouldTiltLabels: false,
    }
  }

  render () {
    return (
      <HeadedChartLayout>
        <HeadedChartLayoutComponent.HeaderMarker>
          { this.renderHeader() }
        </HeadedChartLayoutComponent.HeaderMarker>
        <HeadedChartLayoutComponent.ChartMarker>
          { this.renderChart() }
        </HeadedChartLayoutComponent.ChartMarker>
        { this.hasLegend && (
          <HeadedChartLayoutComponent.LegendMarker>
            { this.renderLegend() }
          </HeadedChartLayoutComponent.LegendMarker>
        ) }
      </HeadedChartLayout>
    )
  }

  renderHeader () {
    const {
      memberActualValues,
      peerActualValues,
      otherActualValues,
      memberProjectedValue,
      peerProjectedValue,
      otherProjectedValue,
      selectedPeergroupName,
      withComparison,
    } = this.props
    const { selectedDate, defaultSelectedDate } = this.state

    const currentSelectedDate = selectedDate || defaultSelectedDate

    const selectedIndex = memberActualValues.findIndex(({ date }) => date === currentSelectedDate)
    const isCurrentMonth = selectedIndex === this.barsCount - 1
    // we can't uppercase it with css because there is a line chart with it's own capitalizing rules
    const month = moment(currentSelectedDate).format('MMMM').toUpperCase()

    return (
      <ChartHeader
        twoRows={ this.hasProjected }
        title={ month }
        member={{
          label: 'You',
          value: formatUSCurrency(memberActualValues[selectedIndex].amount),
          secondaryValue: isCurrentMonth && memberProjectedValue
            ? formatUSCurrency(memberProjectedValue.amount)
            : undefined,
        }}
        peers={{
          label: 'Your Peers',
          value: formatUSCurrency(peerActualValues[selectedIndex].amount),
          secondaryValue: isCurrentMonth && peerProjectedValue
            ? formatUSCurrency(peerProjectedValue.amount)
            : undefined,
        }}
        others={{
          label: selectedPeergroupName,
          value: formatUSCurrency(otherActualValues[selectedIndex].amount),
          secondaryValue: isCurrentMonth && otherProjectedValue
            ? formatUSCurrency(otherProjectedValue.amount)
            : undefined,
        }}
        secondaryTitle={ isCurrentMonth ? 'Projections' : undefined }
        withComparison={ withComparison }
      />
    )
  }

  renderLegend () {
    const {
      limitLine,
      compareLine,
    } = this.props

    const compareLineLegend: LineLegend | null = compareLine ? {
      ...compareLine,
      style: 'dashed',
    } : null
    const limitLineLegend: LineLegend | null = limitLine ? {
      ...limitLine,
      style: 'solid',
    } : null

    const lines: LineLegend[] = reject([
      compareLineLegend,
      limitLineLegend,
    ], isNull)

    return (
      <LineChartLegend lines={ lines } />
    )
  }

  renderChart = () => {
    const {
      memberActualValues,
      memberProjectedValue,
      peerActualValues,
      peerProjectedValue,
      otherActualValues,
      otherProjectedValue,
      limitLine,
      compareLine,
      isMobile,
      withComparison,
    } = this.props
    const { selectedDate, shouldTiltLabels } = this.state

    const memberActualAmounts = memberActualValues.map((p) => p.amount)

    const allValues = reject([
      ...memberActualAmounts,
      memberProjectedValue?.amount ?? [],

      ...withComparison ? [
        ...peerActualValues.map((p) => p.amount),
        peerProjectedValue?.amount ?? [],

        ...otherActualValues.map((p) => p.amount),
        otherProjectedValue?.amount ?? [],
      ] : [],

      limitLine?.amount ?? [],
      compareLine?.amount ?? [],

      0,
    ], isNull)

    const max = Math.max(...allValues)
    const min = Math.min(...allValues)
    const totalValueRange = max - min
    const getBarHeight = (value: number) => totalValueRange !== 0 ? value / totalValueRange * 100 : 0
    const getOffsetTop = (value: number) => totalValueRange !== 0 ? 100 - (value - min) / totalValueRange * 100 : 0
    const highestBarHeight = getBarHeight(max)
    const lowestBarHeight = getBarHeight(min)
    const axisTop = min >= 0
      ? '100%'
      : (
        max > 0
          ? `${ highestBarHeight }%`
          : '0'
      )
    const positionOffset = min < 0 ? lowestBarHeight : 0
    const chartClassName = cn(styles.monthlyBarChart, {
      [styles.narrowBars]: this.barsCount > MAX_BARS_BEFORE_CHART_PADDING,
      [styles.padded]: !isMobile && Boolean(limitLine || compareLine),
      [styles.withLegend]: this.hasLegend,
      [styles.withProjections]: this.hasProjected,
    })


    return (
      <figure
        className={ chartClassName }
        ref={ (node) => this.chartContainerNode = node }

        onMouseLeave={ this.deselectMonth }
      >
        <div
          className={ cn(styles.bars, {
            [styles.tiltedLabels]: shouldTiltLabels,
          }) }
        >
          <span className={ cn(styles.lineWrapper, styles.axis) } style={{ top: axisTop }} />
          <MonthlyBarChartLines
            limitLine={ limitLine }
            compareLine={ compareLine }
            isMobile={ isMobile }
            getOffsetTop={ getOffsetTop }
          />
          {
            memberActualValues.map((memberActualValue, idx) => {
              const hideValues = this.barsCount > MAX_BARS_BEFORE_HIDDEN_LABELS && idx !== this.barsCount - 1
              const isCurrentMonth = idx === this.barsCount - 1

              const memberValue: AmountProjectedPair = {
                amount: memberActualValue.amount,
                projected: isCurrentMonth && memberProjectedValue ? memberProjectedValue.amount : undefined,
              }
              const peerValue: AmountProjectedPair = {
                amount: peerActualValues[idx].amount,
                projected: isCurrentMonth && peerProjectedValue ? peerProjectedValue.amount : undefined,
              }
              const otherValue: AmountProjectedPair = {
                amount: otherActualValues[idx].amount,
                projected: isCurrentMonth && otherProjectedValue ? otherProjectedValue.amount : undefined,
              }
              const monthWrapperClassName = cn(styles.singleMonthWrapper, {
                [styles.selectedSingleMonthWrapper]: selectedDate === null || memberActualValue.date === selectedDate,
              })

              const handleHover = () => {
                this.selectMonth(memberActualValue.date)
              }

              const handleTap = () => {
                if (memberActualValue.date === selectedDate) {
                  this.deselectMonth()
                } else {
                  handleHover()
                }
              }


              return (
                <Touchable
                  key={ idx }
                  onTap={ handleTap }
                >
                  <div
                    className={ monthWrapperClassName }
                    onMouseEnter={ handleHover }
                  >
                    <SingleMonthBarChart
                      date={ memberActualValue.date }

                      memberValue={ memberValue.amount }
                      peerValue={ peerValue.amount }
                      otherValue={ otherValue.amount }

                      memberProjected={ memberValue.projected }
                      peerProjected={ peerValue.projected }
                      otherProjected={ otherValue.projected }

                      offset={ positionOffset }
                      getBarHeight={ getBarHeight }
                      hideValues={ hideValues }
                      noValues={ isMobile }

                      withComparison={ withComparison }
                    />
                  </div>
                </Touchable>
              )
            })
          }
        </div>
      </figure>
    )
  }

  componentDidMount () {
    if (!this.chartContainerNode) {
      return
    }

    this.setSizes()
    this.adjustBarLabels()
    window.addEventListener('resize', this.handleResize)

    this.chartContainerNode.addEventListener('touchmove', this.handleTouchMove, { passive: false } as any)
  }

  componentDidUpdate (prevProps: MonthlyBarChartProps) {
    if (this.props.memberActualValues.length !== prevProps.memberActualValues.length) {
      this.setSizes()
      this.adjustBarLabels()
    }
  }

  componentWillUnmount () {
    window.removeEventListener('resize', this.handleResize)
    this.handleResize.cancel()

    if (this.chartContainerNode) {
      this.chartContainerNode.removeEventListener('touchmove', this.handleTouchMove, { passive: false } as any)
    }
  }

  private setSizes = () => {
    if (!this.chartContainerNode) {
      return
    }

    this.chartRect = this.chartContainerNode.getBoundingClientRect()
    this.barWidth = this.chartRect.width / this.barsCount
  }

  private adjustBarLabels = () => {
    if (!this.chartContainerNode) {
      return
    }

    const shouldTiltLabels = this.barWidth < CHART_LABELS_TILT_THRESHOLD
    if (shouldTiltLabels !== this.state.shouldTiltLabels) {
      this.setState({
        shouldTiltLabels,
      })
    }
  }

  private selectMonth = (selectedDate: string) => {
    this.setState({ selectedDate })
  }

  private deselectMonth = () => {
    this.setState({
      selectedDate: null,
    })
  }

  private handleResize = debounce(() => {
    this.setSizes()
    this.adjustBarLabels()
  }, 50)

  private handleHorizontalTouchMove = (clientX: number) => {
    const offsetX = clientX - this.chartRect.left
    const barIndex = Math.floor(offsetX / this.barWidth)
    const value = this.props.memberActualValues[barIndex]

    if (!value) {
      return
    }

    this.selectMonth(value.date)
  }

  private handleTouchMove = createTouchMoveXHandler(this.handleHorizontalTouchMove)
}

export default MonthlyBarChart
